<template>
  <div class="sidebar-container" :class="{ 'collapse': !sidebar.opened }">
    <!-- 背景装饰 -->
    <div class="sidebar-bg-orb sidebar-bg-orb--top" />
    <div class="sidebar-bg-orb sidebar-bg-orb--bottom" />
    <div class="sidebar-grid" />

    <!-- Logo -->
    <div class="sidebar-logo">
      <div class="logo-icon-wrap">
        <i class="el-icon-monitor logo-icon" />
        <div class="logo-icon-ring" />
      </div>
      <transition name="sidebarLogoFade">
        <span v-if="sidebar.opened" class="logo-title">{{ siteName }}</span>
      </transition>
    </div>

    <!-- 菜单 -->
    <el-scrollbar wrap-class="scrollbar-wrapper">
      <el-menu
        :default-active="activeMenu"
        :collapse="!sidebar.opened"
        :collapse-transition="false"
        :unique-opened="false"
        mode="vertical"
        background-color="transparent"
        text-color="rgba(190,210,240,0.85)"
        active-text-color="#ffffff"
      >
        <sidebar-item
          v-for="route in routes"
          :key="route.path"
          :item="route"
          :base-path="route.path"
        />
      </el-menu>
    </el-scrollbar>
  </div>
</template>

<script>
import SidebarItem from './SidebarItem.vue'
import { mapGetters } from 'vuex'

export default {
  name: 'Sidebar',
  components: { SidebarItem },
  computed: {
    ...mapGetters(['sidebar', 'permission_routes', 'siteName']),
    activeMenu() {
      const { meta, path } = this.$route
      if (meta.activeMenu) return meta.activeMenu
      return path
    },
    routes() {
      return this.permission_routes.filter(r => !r.hidden)
    }
  }
}
</script>

<style lang="scss" scoped>
.sidebar-container {
  width: 210px;
  height: 100%;
  position: fixed;
  left: 0;
  top: 0;
  z-index: 1001;
  overflow: hidden;
  background: linear-gradient(170deg, #1a1f3c 0%, #152238 50%, #0f2d4a 100%);
  transition: width 0.3s;

  &.collapse {
    width: 64px;

    ::v-deep .el-menu {
      width: 64px !important;
      overflow: hidden;
    }
    ::v-deep .el-submenu .el-menu-item {
      min-width: 64px !important;
    }
    ::v-deep .el-menu-item span,
    ::v-deep .el-submenu__title span {
      display: none !important;
      width: 0 !important;
      overflow: hidden !important;
    }
    ::v-deep .el-submenu__icon-arrow {
      display: none !important;
    }
    /* 收起时：取消左右 margin，图标在 64px 内居中 */
    ::v-deep .el-menu-item,
    ::v-deep .el-submenu__title {
      margin: 3px 0 !important;
      width: 64px !important;
      padding: 0 !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
    }

    .logo-title { display: none; }
  }

  /* 去除右侧边框 */
  ::v-deep .el-menu {
    border-right: none !important;
    background: transparent !important;
  }

  /* 图标间距 */
  ::v-deep .el-submenu [class^=el-icon-],
  ::v-deep .el-menu-item [class^=el-icon-] {
    margin-right: 0 !important;
  }

  /* 滚动区高度 */
  ::v-deep .el-scrollbar {
    height: calc(100vh - 60px);
  }
  ::v-deep .el-scrollbar__wrap {
    overflow-x: hidden !important;
    overflow-y: auto !important;
  }
  ::v-deep .el-scrollbar__bar.is-horizontal {
    display: none !important;
  }
  ::v-deep .el-scrollbar__bar.is-vertical {
    opacity: 0;
    transition: opacity 0.3s;
    right: 2px;
    width: 3px;
  }
  &:hover ::v-deep .el-scrollbar__bar.is-vertical {
    opacity: 1;
  }

  /* 菜单项样式 */
  ::v-deep .el-menu-item,
  ::v-deep .el-submenu__title {
    height: 46px;
    line-height: 46px;
    margin: 3px 10px;
    border-radius: 8px;
    width: calc(100% - 20px) !important;
    transition: all 0.22s ease !important;
    position: relative;
    overflow: hidden;

    &:hover {
      background: rgba(255, 255, 255, 0.08) !important;
      color: #fff !important;
    }
  }

  /* 激活菜单项 */
  ::v-deep .el-menu-item.is-active {
    background: #409eff !important;
    color: #fff !important;
    font-weight: 600;
    box-shadow: 0 4px 14px rgba(64, 158, 255, 0.45);
  }

  /* 子菜单弹出层 */
  ::v-deep .el-submenu__title {
    &:hover { background: rgba(64, 158, 255, 0.1) !important; }
  }

  /* 子菜单展开背景 */
  ::v-deep .el-submenu .el-menu {
    background: rgba(0, 0, 0, 0.15) !important;
    border-radius: 0 0 8px 8px;
    margin: 0 8px;
    width: calc(100% - 16px) !important;
    padding-bottom: 4px;
  }

  /* 子菜单项缩进 */
  ::v-deep .el-submenu .el-menu-item {
    padding-left: 44px !important;
    margin: 2px 0 !important;
    width: 100% !important;
    border-radius: 6px;
  }
}

/* 背景装饰光晕 */
.sidebar-bg-orb {
  position: absolute;
  border-radius: 50%;
  pointer-events: none;
  z-index: 0;
  filter: blur(60px);
}
.sidebar-bg-orb--top {
  width: 240px;
  height: 240px;
  background: radial-gradient(circle, rgba(64,158,255,0.18) 0%, transparent 70%);
  top: -80px;
  right: -80px;
  animation: orb-pulse 6s ease-in-out infinite;
}
.sidebar-bg-orb--bottom {
  width: 200px;
  height: 200px;
  background: radial-gradient(circle, rgba(54,207,201,0.12) 0%, transparent 70%);
  bottom: -60px;
  left: -60px;
  animation: orb-pulse 8s ease-in-out infinite reverse;
}

@keyframes orb-pulse {
  0%, 100% { transform: scale(1); opacity: 1; }
  50%       { transform: scale(1.15); opacity: 0.7; }
}

/* 网格背景纹理 */
.sidebar-grid {
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  background-image:
    linear-gradient(rgba(64,158,255,0.04) 1px, transparent 1px),
    linear-gradient(90deg, rgba(64,158,255,0.04) 1px, transparent 1px);
  background-size: 30px 30px;
  mask-image: linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.6) 30%, rgba(0,0,0,0.6) 70%, transparent 100%);
}

/* Logo 区域 */
.sidebar-logo {
  height: 60px;
  display: flex;
  align-items: center;
  padding: 0 16px;
  position: relative;
  z-index: 1;
  overflow: hidden;
  border-bottom: 1px solid rgba(64,158,255,0.15);
  background: rgba(0,0,0,0.15);

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 10%;
    right: 10%;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(64,158,255,0.5), transparent);
  }

  .logo-icon-wrap {
    position: relative;
    width: 34px;
    height: 34px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #1a6fd4, #409eff, #36cfc9);
    border-radius: 50%;
    box-shadow: 0 0 14px rgba(64,158,255,0.5), 0 0 28px rgba(64,158,255,0.15);
    animation: logo-glow 3s ease-in-out infinite;

    .logo-icon {
      font-size: 17px;
      color: #fff;
      position: relative;
      z-index: 1;
    }

    .logo-icon-ring {
      position: absolute;
      inset: -5px;
      border-radius: 50%;
      border: 1.5px solid transparent;
      background: linear-gradient(135deg, rgba(64,158,255,0.6), rgba(54,207,201,0.2)) border-box;
      -webkit-mask: linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0);
      -webkit-mask-composite: destination-out;
      mask-composite: exclude;
      animation: ring-spin 4s linear infinite;
    }
  }

  .logo-title {
    margin-left: 12px;
    color: #fff;
    font-size: 15px;
    font-weight: 700;
    white-space: nowrap;
    letter-spacing: 0.5px;
    text-shadow: 0 0 12px rgba(64,158,255,0.4);
  }
}

@keyframes logo-glow {
  0%, 100% { box-shadow: 0 0 14px rgba(64,158,255,0.5), 0 0 28px rgba(64,158,255,0.15); }
  50%       { box-shadow: 0 0 20px rgba(64,158,255,0.7), 0 0 40px rgba(64,158,255,0.25); }
}

@keyframes ring-spin {
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
}

/* Logo 文字淡入淡出 */
.sidebarLogoFade-enter-active {
  transition: opacity 0.15s 0.2s;
}
.sidebarLogoFade-enter,
.sidebarLogoFade-leave-to {
  opacity: 0;
}
</style>

<!-- 全局样式：修复折叠后弹出子菜单背景（挂载在 body，scoped 无法穿透） -->
<style lang="scss">
.el-menu--popup-container .el-menu--popup,
.el-menu--popup {
  background: #152238 !important;
  border: 1px solid rgba(64, 158, 255, 0.2) !important;
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.45) !important;
  border-radius: 8px !important;
  padding: 4px !important;
  min-width: 140px !important;

  .el-menu-item {
    height: 42px !important;
    line-height: 42px !important;
    border-radius: 6px !important;
    color: rgba(190, 210, 240, 0.85) !important;
    margin: 2px 0 !important;

    &:hover {
      background: rgba(255, 255, 255, 0.08) !important;
      color: #fff !important;
    }

    &.is-active {
      background: #409eff !important;
      color: #fff !important;
      font-weight: 600 !important;
      box-shadow: 0 4px 14px rgba(64, 158, 255, 0.45) !important;
    }
  }
}
</style>
